// frontend/src/lib/ton-format.ts
// CF-WITHDRAW-DISPLAY-PRECISION-V1 (PL #7211; Scorer design-alignment 2026-05-31 1229Z).
//
// Canonical, EXACT custodial-TON amount formatter. PL withdrew exactly 4.325 TON and
// the app rendered "4.32" — a 2-decimal truncation that breaks trust in a money
// surface. The bug lived in FOUR duplicated component-local formatters (App
// `formatNano`, CoinFlip `formatTonBalance`, and an identical `nanoToTon` in
// WithdrawModal / History / Verify), plus a 1-decimal signed-profit truncation in
// History. This module is the single source of truth.
//
// Policy (Scorer-aligned): show the EXACT value — no truncation, no rounding, no cap —
// with a MINIMUM of 2 decimals and up to the full 9 nano digits. The displayed amount
// must never differ from what moved on-chain; layout adapts to exact values, the value
// is never shortened.
//
// Fail-loud (R6 Binding 6): a malformed economic string must NEVER become a plausible
// zero or a plausible TON string. All string validation lives in `parseNanoString` /
// `parseTonAmount`; `formatTonExact` takes a `bigint` only, so a raw `BigInt(string)`
// coercion is never the safety boundary.

const NANO_PER_TON = 1_000_000_000n;

export type TonAmountResult = { ok: true; ton: string } | { ok: false; reason: string };

// Strict nano-string parser. Accepts ONLY a non-negative integer string of ASCII
// digits (the canonical nano wire form). Rejects empty/whitespace, decimal points,
// signs, scientific notation, and any non-digit. Returns null on rejection — callers
// map null to an explicit invalid-data state, never a coerced zero.
export function parseNanoString(nano: string): bigint | null {
    if (typeof nano !== 'string') return null;
    if (!/^[0-9]+$/.test(nano)) return null; // rejects '', ' ', '-5', '1.5', '1e9', '0x..', '12a'
    try {
        return BigInt(nano);
    } catch {
        return null;
    }
}

// Format a nano amount as an EXACT TON string: full precision, trailing zeros trimmed,
// MINIMUM 2 decimals. `bigint`-only (validated upstream). Defensive on sign though
// custodial balances are non-negative; `formatSignedTonExact` is the signed display.
//   4325000000n -> "4.325"      1450000000n -> "1.45"        2000000000n -> "2.00"
//   100000000n  -> "0.10"       1234567000n -> "1.234567"    1n -> "0.000000001"   0n -> "0.00"
export function formatTonExact(nano: bigint): string {
    const negative = nano < 0n;
    const abs = negative ? -nano : nano;
    const whole = abs / NANO_PER_TON;
    let frac = (abs % NANO_PER_TON).toString().padStart(9, '0').replace(/0+$/, '');
    if (frac.length < 2) frac = frac.padEnd(2, '0'); // never fewer than 2 decimals
    return `${negative ? '-' : ''}${whole.toString()}.${frac}`;
}

// Signed exact display for net profit/loss (History). Non-negative (incl. zero) gets
// '+', negative gets '-'; magnitude is the exact formatter.
//   +4325000000n -> "+4.325"   -1500000000n -> "-1.50"   0n -> "+0.00"
export function formatSignedTonExact(nano: bigint): string {
    const sign = nano < 0n ? '-' : '+';
    const abs = nano < 0n ? -nano : nano;
    return `${sign}${formatTonExact(abs)}`;
}

// Fail-loud string entry point for money surfaces (withdrawal / history / verify /
// informational balance). On rejection the caller renders an explicit invalid state.
export function parseTonAmount(nano: string): TonAmountResult {
    const n = parseNanoString(nano);
    if (n === null) return { ok: false, reason: 'malformed_nano' };
    return { ok: true, ton: formatTonExact(n) };
}

export type TonToNanoResult = { ok: true; nano: bigint } | { ok: false; reason: string };

// CF-DEPOSIT-CUSTOM-AMOUNT-V1 (PL #7245). Strict TON-decimal-string → nano `bigint`
// parser — the lexical inverse of `formatTonExact`, done with EXACT string math, NEVER
// `Number(x) * 1e9`. The hazard there is not rounding (Math.round recovers the small
// pre-round binary-float drift in this range), it is SILENT COERCION: parseFloat/Number
// quietly accept malformed input (parseFloat('7.5abc') === 7.5, Number('') === 0) and
// drop precision past 9 decimals — turning a typo into a wrong deposit. This parser
// rejects all of that loudly. Accepts a single non-negative decimal: one or more
// whole digits, optional '.' followed by 1–9 fractional digits. Rejects empty/whitespace,
// leading-dot ('.5'), trailing-dot ('5.'), signs, exponents, multiple dots, >9 fractional
// digits, and any non-digit. Range/min/max are the CALLER's policy — this is purely lexical.
// Fail-loud (#7233): rejection returns {ok:false,reason}; NEVER a coerced zero, so a
// malformed entry can never silently become a 0-TON (or any other plausible) deposit.
//   "1" -> 1000000000n   "0.5" -> 500000000n   "7.55" -> 7550000000n
//   "0.000000001" -> 1n   ""/".5"/"5."/"-1"/"1e9"/"1.0000000001" -> {ok:false}
export function parseTonToNano(input: string): TonToNanoResult {
    if (typeof input !== 'string') return { ok: false, reason: 'not_a_string' };
    const s = input.trim();
    if (s === '') return { ok: false, reason: 'empty' };
    if (!/^[0-9]+(\.[0-9]+)?$/.test(s)) return { ok: false, reason: 'malformed' };
    const [whole, frac = ''] = s.split('.');
    if (frac.length > 9) return { ok: false, reason: 'too_precise' };
    const nano = BigInt(whole) * NANO_PER_TON + BigInt(frac.padEnd(9, '0'));
    return { ok: true, nano };
}
